/*
  Accessibility corrections for third-party injected widgets.

  The Wholescale reviews/Q&A widget injects its markup into
  .review-widget-injection after page load, so none of this can be fixed in
  the theme source. We watch that one container (there is no ready event
  published by the widget) and correct three things as its DOM appears:

  1. WCAG 4.1.3 — the confirmation toast has neither role nor aria-live, so it
     appears silently for screen-reader users. It is persistent in the DOM, so
     the role is set once, up front, before any text change - a live region
     must already carry its role when its contents change or the announcement
     is missed.

  2. WCAG 1.3.5 — the review form's name and email fields carry no autocomplete
     tokens, so assistive tech and browsers cannot identify their purpose.

  3. WCAG 2.4.3 — both form drawers (write-review and question-form) are
     parked off-screen while remaining visibility:visible, so their sixteen
     fields stay in the tab order and a keyboard user wades through a form
     they cannot see. Each drawer is inert unless it carries the widget's own
     'active' open-state class. An earlier version keyed this off an
     IntersectionObserver instead; geometry proved wrong in both directions -
     a drawer flush with the viewport edge read as visible in some sessions
     (fields stayed tabbable), and the observer could lag the open animation
     (an OPEN form stayed inert). The vendor's state class is the signal that
     is right by definition.

  4. WCAG 2.1.1 — each form's "Add Photo" control is a <label> for a
     display:none file input. Labels are not in the tab order and hidden
     inputs cannot take focus, so the control is mouse-only. The label is
     given button semantics and an Enter/Space activation that forwards to
     its native click, which opens the file picker.

  5. WCAG 2.4.3 — the open form presents as a modal dialog (overlay, page
     behind it), but Tab past its last control walked out into the page with
     the form still open, and Escape did nothing. While a drawer is active,
     Tab is contained (last wraps to first, Shift+Tab mirrors) and Escape
     closes it via the widget's own close control.

  6. WCAG 2.1.1 — the close control itself is a <span>. The widget makes it
     focusable, but a span does not fire its click on Enter the way a real
     button does, so it could be reached and not operated. Same correction as
     the Add Photo label: button semantics plus key forwarding.
*/
(function () {
  var container = document.querySelector('.review-widget-injection');
  if (!container) return;

  var AUTOCOMPLETE = {
    first_name: 'given-name',
    last_name: 'family-name',
    email: 'email'
  };

  function syncDrawer(drawer) {
    if (drawer.classList.contains('active')) drawer.removeAttribute('inert');
    else drawer.setAttribute('inert', '');
  }

  function applyFixes() {
    var toast = container.querySelector('.wholescale-toast');
    if (toast && !toast.getAttribute('role')) toast.setAttribute('role', 'status');

    Object.keys(AUTOCOMPLETE).forEach(function (name) {
      var field = container.querySelector('[name="' + name + '"]');
      if (field && !field.getAttribute('autocomplete')) {
        field.setAttribute('autocomplete', AUTOCOMPLETE[name]);
      }
    });

    container.querySelectorAll('.widget-form-drawer').forEach(syncDrawer);

    container.querySelectorAll('label.add-photo:not([role]), .widget-drawer-close:not([role])').forEach(function (control) {
      control.setAttribute('role', 'button');
      control.setAttribute('tabindex', '0');
    });
  }

  container.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    var control = event.target.closest && event.target.closest('label.add-photo, .widget-drawer-close');
    if (!control) return;
    event.preventDefault();
    control.click();
  });

  function activeDrawer() {
    return container.querySelector('.widget-form-drawer.active');
  }

  function drawerFocusables(drawer) {
    return [].filter.call(
      drawer.querySelectorAll('a[href], button, input, select, textarea, [tabindex="0"]'),
      function (el) { return el.offsetWidth || el.offsetHeight; }
    );
  }

  container.addEventListener('keydown', function (event) {
    if (event.key !== 'Tab') return;
    var drawer = activeDrawer();
    if (!drawer || !drawer.contains(document.activeElement)) return;
    var focusables = drawerFocusables(drawer);
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* document, not container: focus can already be back in the page while the
     form is still open - exactly the state being corrected. */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var drawer = activeDrawer();
    if (!drawer) return;
    var close = drawer.querySelector('.widget-drawer-close');
    if (close) close.click();
  });

  applyFixes();
  /* childList catches the widget injecting its DOM; attributes/class catches
     the drawers toggling their 'active' open state. */
  new MutationObserver(applyFixes).observe(container, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class']
  });
})();
